import { useState, useEffect, ReactNode } from 'react';

interface PasswordProtectionProps {
  children: ReactNode;
}

export function PasswordProtection({ children }: PasswordProtectionProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already authenticated on mount
  useEffect(() => {
    // Check if we're on production URLs - bypass password protection
    // TODO: Ideally these should be environment variables
    if (['app.birdiegifts.com', 'birdie-live-c4536d2037f0.herokuapp.com'].includes(window.location.hostname)) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Check for secret query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const secretKey = urlParams.get('secret');

    if (secretKey === 'bypass-password-protection') {
      setIsAuthenticated(true);
      localStorage.setItem('birdie-dev-auth', 'authenticated');
      setIsLoading(false);
      return;
    }

    const savedAuth = localStorage.getItem('birdie-dev-auth');
    if (savedAuth === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('/api/dev/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const result = await response.json();

      if (result.success && result.authenticated) {
        setIsAuthenticated(true);
        localStorage.setItem('birdie-dev-auth', 'authenticated');
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch (error) {
      console.error('Password verification error:', error);
      setError('Unable to verify password. Please try again.');
      setPassword('');
    }
  };

  // Show loading state briefly
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pastel-mint via-pastel-sage to-pastel-lavender flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-soft-charcoal border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Show password form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pastel-mint via-pastel-sage to-pastel-lavender flex items-center justify-center p-4">
        <div className="max-w-md w-full mx-auto bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft p-8">

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-soft-charcoal mb-2">Development Access</h1>
            <p className="text-soft-slate">This application is in development. Please enter the access password.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-soft-charcoal mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors bg-white/50 text-soft-charcoal ${
                  error 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-pastel-lavender focus:border-pastel-purple'
                }`}
                placeholder="Enter password"
                autoFocus
              />
              {error && (
                <p className="text-red-600 text-sm mt-2">{error}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-pastel-purple to-pastel-pink hover:from-pastel-pink hover:to-pastel-purple text-soft-charcoal font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer border-2 border-pastel-purple/50 hover:border-pastel-pink/60"
            >
              Access Application
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-soft-slate">
              🔒 This is a development environment protection
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render the app if authenticated
  return <>{children}</>;
}